import type { Container } from "pixi.js";
import type { GridModel } from "../../../grid/GridModel";
import type { PresentationTaskRunner } from "../../../runner/PresentationTaskRunner";
import type { TweenEasing } from "../../../runner/Easing";
import type { GridLayout, ComputedGridLayout, GridPoint } from "../GridLayout";
import type { GridSymbolPool } from "../GridSymbolPool";
import type { SymbolView } from "../SymbolView";
import type { CellRegistry } from "../GridLayer";

const distance = (a: { x: number; y: number }, b: { x: number; y: number }): number => Math.hypot(a.x - b.x, a.y - b.y);

/** The stagger for a caller that names none: one pair, read by the planned drop and the instant plan. */
const DEFAULT_COLUMN_STAGGER_SECONDS = 0.06;
const DEFAULT_WITHIN_COLUMN_STAGGER_SECONDS = 0.02;

export type PreparedDropTween = {
  node: Container;
  targetPosition: GridPoint;
  durationSeconds: number;
  kind: "survivor_fall" | "spawn_drop";
  col: number;
  /** target row */
  row: number;
  startOrderInColumn: number;
};

export type PreparedDropPlan = {
  nextModel: GridModel;
  newSymbolsByIndex: Map<number, SymbolView>;
  tweens: PreparedDropTween[];
  strict: boolean;
  columnStaggerSeconds: number;
  withinColumnStaggerSeconds: number;
  minTriggerCol: number;
};

/**
 * The gravity drop after a removal — survivors fall into the vacated cells, fresh symbols drop in
 * from above to refill each column — shared by every mechanic that removes and refills (the tumbling
 * reels, the cascade). Plans against a `CellRegistry` and the grid's pool + layout; animates on the
 * `PresentationTaskRunner` under the scope its mechanic names, so every drop scales with the speed
 * mode and pauses / frame-steps with the round (plan step 5, pass 4).
 *
 * `prepareDropToModel` plans the tweens and rebinds the cells immediately (the grid is live before
 * the animation runs); `animatePreparedDrop` drives them. The removal shrink stays with the mechanic.
 */
export class GravityDrop {
  private readonly cells: CellRegistry;
  private readonly pool: GridSymbolPool;
  private readonly layoutUtil: GridLayout;
  private readonly scope: string;

  public constructor(input: { cells: CellRegistry; pool: GridSymbolPool; layoutUtil: GridLayout; scope: string }) {
    this.cells = input.cells;
    this.pool = input.pool;
    this.layoutUtil = input.layoutUtil;
    this.scope = input.scope;
  }

  /**
   * Plan the drop: per column, survivors (cells NOT in the removal mask) fall to the bottom rows in
   * order; the remaining top cells are spawned fresh from the pool above the grid edge. Rebinds the
   * cells to the post-drop mapping immediately.
   */
  public prepareDropToModel(input: {
    removedPositionMask: string;
    nextModel: GridModel;

    gridColumnCount: number;
    gridRowCount: number;
    layout: ComputedGridLayout;

    fallDurationSeconds?: number; // fallback only (distance-based when a speed is set)
    spawnDurationSeconds?: number; // fallback only

    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;

    strictSymbolConsistencyCheck?: boolean;
  }): PreparedDropPlan {
    const { removedPositionMask, nextModel } = input;

    const columnStaggerSeconds = input.columnStaggerSeconds ?? DEFAULT_COLUMN_STAGGER_SECONDS;
    const withinColumnStaggerSeconds = input.withinColumnStaggerSeconds ?? DEFAULT_WITHIN_COLUMN_STAGGER_SECONDS;
    const strict = input.strictSymbolConsistencyCheck ?? false;

    const expectedMaskLen = input.gridColumnCount * input.gridRowCount;
    if (removedPositionMask.length !== expectedMaskLen) {
      throw new Error(`GravityDrop.prepareDropToModel mask length must be ${expectedMaskLen}, got ${removedPositionMask.length}.`);
    }

    if (nextModel.columnCount !== input.gridColumnCount || nextModel.rowCount !== input.gridRowCount) {
      throw new Error(
        `GravityDrop.prepareDropToModel model size mismatch. Grid=${input.gridColumnCount}x${input.gridRowCount}, ` +
          `model=${nextModel.columnCount}x${nextModel.rowCount}`,
      );
    }

    // Leftmost column with a removal — the L→R stagger anchor.
    let minTriggerCol = input.gridColumnCount;
    for (let i = 0; i < removedPositionMask.length; i++) {
      if (removedPositionMask[i] === "1") {
        minTriggerCol = Math.min(minTriggerCol, i % input.gridColumnCount);
      }
    }
    if (minTriggerCol === input.gridColumnCount) minTriggerCol = 0;

    const fallDurationFallback = input.fallDurationSeconds ?? 0.25;
    const spawnDurationFallback = input.spawnDurationSeconds ?? 0.28;

    // originY is the centre of row 0 (top row); the spawn stack starts one cell above the top edge.
    const topEdgeY = input.layout.originY - input.layout.cellHeight * 0.5; // above row 0 (Pixi y-down)

    const newSymbolsByIndex: Map<number, SymbolView> = new Map();
    const tweens: PreparedDropTween[] = [];

    for (let col = 0; col < input.gridColumnCount; col++) {
      const survivors: SymbolView[] = [];

      // Collect survivors bottom→top so the bottom-most survivor lands first.
      for (let row = input.gridRowCount - 1; row >= 0; row--) {
        const index = this.layoutUtil.rowColToIndex(input.gridColumnCount, row, col);
        if (removedPositionMask[index] === "1") continue;
        const view = this.cells.cellAt(index);
        if (!view) continue;
        if (!view.node.visible) continue;
        survivors.push(view);
      }

      let startOrderInColumn = 0;
      let targetRow = input.gridRowCount - 1;

      // Survivors gravity-fall into the bottom rows.
      for (const survivor of survivors) {
        const targetIndex = this.layoutUtil.rowColToIndex(input.gridColumnCount, targetRow, col);
        const targetPos = this.layoutUtil.computeLocalPosition(input.layout, targetRow, col);

        if (strict) {
          const expectedId = nextModel.cells[targetRow][col]?.symbolId ?? "?";
          if ((survivor.getSymbolId() ?? null) !== expectedId) {
            // Defensive: keep the cell consistent with the authoritative model.
            this.pool.applySymbolIdToView(survivor, expectedId);
          }
        }

        newSymbolsByIndex.set(targetIndex, survivor);
        tweens.push({
          node: survivor.node,
          targetPosition: targetPos,
          durationSeconds: fallDurationFallback,
          kind: "survivor_fall",
          col,
          row: targetRow,
          startOrderInColumn,
        });

        startOrderInColumn++;
        targetRow--;
      }

      // Spawn the missing top cells above the grid edge; they drop in to refill.
      let spawnIndexInColumn = 0;
      for (; targetRow >= 0; targetRow--, spawnIndexInColumn++) {
        const targetIndex = this.layoutUtil.rowColToIndex(input.gridColumnCount, targetRow, col);
        const symbolId = nextModel.cells[targetRow][col]?.symbolId ?? "?";

        const spawned = this.pool.acquire();
        this.pool.applySymbolIdToView(spawned, symbolId);

        const targetPos = this.layoutUtil.computeLocalPosition(input.layout, targetRow, col);
        const spawnY = topEdgeY - input.layout.cellHeight * 0.5 - spawnIndexInColumn * input.layout.cellHeight;

        spawned.node.position.set(targetPos.x, spawnY);
        spawned.node.scale.set(1, 1);
        spawned.node.visible = true;

        newSymbolsByIndex.set(targetIndex, spawned);
        tweens.push({
          node: spawned.node,
          targetPosition: targetPos,
          durationSeconds: spawnDurationFallback,
          kind: "spawn_drop",
          col,
          row: targetRow,
          startOrderInColumn,
        });

        startOrderInColumn++;
      }
    }

    // Rebind the live mapping now — the grid is authoritative for any frame_prepare action that
    // runs before the drop animates.
    this.cells.forgetAll();
    for (const [index, view] of newSymbolsByIndex.entries()) this.cells.adopt(index, view);

    return {
      nextModel,
      newSymbolsByIndex,
      tweens,
      strict,
      columnStaggerSeconds,
      withinColumnStaggerSeconds,
      minTriggerCol,
    };
  }

  /**
   * The plan for a collapse with no rendered grid to drop from: the mechanic has painted `nextModel`
   * already, so nothing moves and `animatePreparedDrop` only runs the strict check. The tumble and the
   * cascade share it, as they share the drop.
   */
  public static instantPlan(
    nextModel: GridModel,
    cells: Map<number, SymbolView>,
    options: { columnStaggerSeconds?: number; withinColumnStaggerSeconds?: number; strictSymbolConsistencyCheck?: boolean },
  ): PreparedDropPlan {
    return {
      nextModel,
      newSymbolsByIndex: cells,
      tweens: [],
      strict: options.strictSymbolConsistencyCheck ?? false,
      columnStaggerSeconds: options.columnStaggerSeconds ?? DEFAULT_COLUMN_STAGGER_SECONDS,
      withinColumnStaggerSeconds: options.withinColumnStaggerSeconds ?? DEFAULT_WITHIN_COLUMN_STAGGER_SECONDS,
      minTriggerCol: 0,
    };
  }

  /**
   * Drive the prepared tweens: per-column L→R stagger, distance-based (or fallback) durations, an
   * optional overshoot-and-settle bounce, one `onColumnDropLanded` per column with real motion (at
   * its earliest landing), and the per-index `symbolEnterGridCallbacks` as each symbol crosses the
   * grid's top edge. Runner-only (engine time); with no runner it snaps to targets instantly.
   */
  public async animatePreparedDrop(input: {
    plan: PreparedDropPlan;
    runner: PresentationTaskRunner | null;
    gridColumnCount: number;
    gridRowCount: number;
    layout: ComputedGridLayout;

    enableOvershoot?: boolean;
    overshootPixels?: number;
    overshootPhaseRatio?: number;
    overshootSettleEasing?: TweenEasing;

    /**
     * Each symbol's time follows its distance at this speed (clamped to the min / max); absent, each
     * symbol takes the plan's flat fall / spawn duration.
     */
    dropSpeedPixelsPerSecond?: number;
    minDropDurationSeconds?: number;
    maxDropDurationSeconds?: number;

    onColumnDropLanded?: (column: number) => void;
    /** Fired once per registered index when that symbol enters the grid (a snapped symbol: at its start delay). */
    symbolEnterGridCallbacks?: Map<number, () => void>;
    strictVerifyIdAtIndex?: (index: number) => string | null;
  }): Promise<void> {
    const { plan, runner } = input;
    const scope = this.scope;

    const enableOvershoot = input.enableOvershoot ?? true;
    const overshootPx = input.overshootPixels ?? 8;
    const overshootPhaseRatio = input.overshootPhaseRatio ?? 0.82;
    const settleEasing: TweenEasing = input.overshootSettleEasing ?? "quadOut";

    const columnStaggerSeconds = plan.columnStaggerSeconds;
    const withinColumnStaggerSeconds = plan.withinColumnStaggerSeconds;
    const minActiveCol = plan.minTriggerCol;

    // Until 2026-09-26 an absent speed meant 2600 px/s, so the flat durations were never read and the
    // tuning docs that offer them were false (review finding 28).
    const dropSpeed = input.dropSpeedPixelsPerSecond;
    const minDur = input.minDropDurationSeconds ?? 0.06;
    const maxDur = input.maxDropDurationSeconds ?? 999;

    const startDelayOf = (t: PreparedDropTween): number =>
      (t.col - minActiveCol) * columnStaggerSeconds + t.startOrderInColumn * withinColumnStaggerSeconds;

    // No animation infra → snap everything to rest and fire the land cadence synchronously.
    if (!runner) {
      // Fire land only for columns with REAL motion — mirror the runner branch's zero-distance
      // skip, so an unremoved column doesn't play a spurious column_drop. Compute before snapping.
      const movedColumns = new Set<number>();
      for (const t of plan.tweens) {
        if (distance(t.node.position, t.targetPosition) >= 0.5) movedColumns.add(t.col);
      }
      for (const t of plan.tweens) t.node.position.set(t.targetPosition.x, t.targetPosition.y);
      if (input.onColumnDropLanded) {
        for (const col of movedColumns) input.onColumnDropLanded(col);
      }
      if (input.symbolEnterGridCallbacks) {
        for (const t of plan.tweens) input.symbolEnterGridCallbacks.get(t.row * input.gridColumnCount + t.col)?.();
      }
      this.strictVerify(input);
      return;
    }

    const computeDuration = (node: Container, target: GridPoint, fallback: number): number => {
      if (dropSpeed === undefined) return fallback;
      const dist = distance(node.position, target);
      if (!Number.isFinite(dist)) return fallback;
      return Math.min(maxDur, Math.max(minDur, dist / Math.max(1, dropSpeed)));
    };

    const tweenWithOptionalOvershoot = async (node: Container, fallbackSeconds: number, target: GridPoint): Promise<void> => {
      const seconds = computeDuration(node, target, fallbackSeconds);
      if (!enableOvershoot) {
        await runner.tweenTo(node, seconds, { position: { x: target.x, y: target.y } }, scope, { easing: "linear" });
        return;
      }
      const t1 = Math.max(0.01, seconds * overshootPhaseRatio);
      const t2 = Math.max(0.01, seconds * (1 - overshootPhaseRatio));
      const overshoot = { x: target.x, y: target.y + overshootPx }; // below the target (Pixi y-down)
      await runner.tweenTo(node, t1, { position: overshoot }, scope, { easing: "linear" });
      await runner.tweenTo(node, t2, { position: { x: target.x, y: target.y } }, scope, { easing: settleEasing });
    };

    /**
     * When a moving symbol crosses the grid's top edge: at its start delay if it starts inside the
     * grid, else the fraction of its overshoot phase spent above the edge.
     */
    const entryDelayOf = (t: PreparedDropTween, delay: number): number => {
      const gridTopY = input.layout.originY - input.layout.cellHeight * 0.5;
      const startY = t.node.position.y;
      if (startY >= gridTopY) return delay;
      const seconds = computeDuration(t.node, t.targetPosition, t.durationSeconds);
      const overshootY = t.targetPosition.y + (enableOvershoot ? overshootPx : 0);
      const phase1Distance = overshootY - startY;
      const distToGridTop = gridTopY - startY;
      const fraction = phase1Distance > 0 ? distToGridTop / phase1Distance : 0;
      const phase1Duration = seconds * (enableOvershoot ? overshootPhaseRatio : 1);
      return delay + fraction * phase1Duration;
    };

    const run = async (): Promise<void> => {
      const ps: Promise<void>[] = [];

      // Per-column land cadence: fire onColumnDropLanded at the EARLIEST landing in each column
      // with real (non-zero-distance) motion — the bottom symbol's settle. Skip zero-distance
      // survivor "tweens" (columns with no removals).
      const firstLandTimeByCol = new Map<number, number>();
      for (const t of plan.tweens) {
        const dist = distance(t.node.position, t.targetPosition);
        if (!Number.isFinite(dist) || dist < 0.5) continue;
        const landTime = startDelayOf(t) + computeDuration(t.node, t.targetPosition, t.durationSeconds);
        const existing = firstLandTimeByCol.get(t.col);
        if (existing === undefined || landTime < existing) firstLandTimeByCol.set(t.col, landTime);
      }
      for (const [col, fireTime] of firstLandTimeByCol.entries()) {
        ps.push(
          (async () => {
            if (fireTime > 0) await runner.sleep(fireTime, scope);
            input.onColumnDropLanded?.(col);
          })(),
        );
      }

      const tweensSorted = [...plan.tweens].sort((a, b) => {
        if (a.col !== b.col) return a.col - b.col;
        return a.startOrderInColumn - b.startOrderInColumn;
      });

      for (const t of tweensSorted) {
        const delay = startDelayOf(t);
        const enterCallback = input.symbolEnterGridCallbacks?.get(t.row * input.gridColumnCount + t.col);

        const dist = distance(t.node.position, t.targetPosition);
        if (dist < 0.5) {
          // Already at target (e.g. an unremoved column's survivors) — snap and skip.
          t.node.position.set(t.targetPosition.x, t.targetPosition.y);
          if (enterCallback) {
            ps.push(
              (async () => {
                if (delay > 0) await runner.sleep(delay, scope);
                enterCallback();
              })(),
            );
          }
          continue;
        }

        if (enterCallback) {
          const entryDelay = entryDelayOf(t, delay);
          ps.push(
            (async () => {
              if (entryDelay > 0) await runner.sleep(entryDelay, scope);
              enterCallback();
            })(),
          );
        }

        ps.push(
          (async () => {
            if (delay > 0) await runner.sleep(delay, scope);
            await tweenWithOptionalOvershoot(t.node, t.durationSeconds, t.targetPosition);
          })(),
        );
      }

      await Promise.all(ps);
    };

    await runner.runInScope(scope, run);

    this.strictVerify(input);
  }

  private strictVerify(input: {
    plan: PreparedDropPlan;
    gridColumnCount: number;
    gridRowCount: number;
    strictVerifyIdAtIndex?: (index: number) => string | null;
  }): void {
    const { plan } = input;
    if (!plan.strict || !input.strictVerifyIdAtIndex) return;
    for (let row = 0; row < input.gridRowCount; row++) {
      for (let col = 0; col < input.gridColumnCount; col++) {
        const index = row * input.gridColumnCount + col;
        const expectedId = plan.nextModel.cells[row][col]?.symbolId ?? "?";
        const actualId = input.strictVerifyIdAtIndex(index);
        if (actualId !== expectedId) {
          throw new Error(`[GravityDrop] strict mismatch at row=${row} col=${col}. actual=${String(actualId)} expected=${expectedId}`);
        }
      }
    }
  }
}
